import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Pie } from '../../themed/charts';
import { getTutorials, setActiveTutorial } from '../../../store/tutorials';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { EXTERNAL_USER_GUIDE_URL, tutorialConstants } from '../../../utils/constants';

import Conditional from '../../common/conditional';
import DashboardLoader from './dashboardLoader';
import DashboardSearchBar from '../../blocks/dashboard-seacrhbar';
import DashboardOverviewItem from './dashboardOverviewItem';
import DBEmptyData from './dashboardEmptyData';
import Instruction from '../../themed/instruction-modals/instruction';
import PDFDownload from '../../themed/pdf-download';
import RecentProjects from '../../blocks/recent-projects';
import Signature from '../../blocks/signature';
import SyncTime from '../../blocks/sync-time';
import WelcomeToExternalUser from '../../themed/instruction-modals/welcomeToExternalUser';

import useExternalDashboard from '../../../hooks/useExternalDashboard';
import config from '../../../utils/config';
import classNames from './dashboard.module.scss';

import * as authService from '../../../services/authService';
import * as dummyData from '../../../utils/data';
import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

import file from '../../../assets/animations/file.json';
import notificationBell from '../../../assets/animations/notifications.json';
import pdf from '../../../assets/animations/pdf.json';
import success from '../../../assets/animations/success.json';
import routes from '../../../utils/routes';

const ExternalUserDashboard: React.FC = () => {
	const dispatch = useAppDispatch();
	const externalDashboard = useExternalDashboard();
	const tutorials = useAppSelector(getTutorials);
	const navigate = useNavigate();

	const getInspectionChartBreakdown = () => {
		const breakdown = externalDashboard.overview.reportByInspectionType;
		if (breakdown.length === 0) return null;

		const labels: string[] = breakdown.map((b: any) => b.inspectionType);
		const data = breakdown.map((b: any) => b.count);
		const backgroundColor = dummyData.getInspectionChartColors();

		return {
			labels,
			datasets: [{ label: 'Inspection Type Breakdown', data, backgroundColor }],
		};
	};

	const getRiskChartBreakdown = () => {
		const ratings = externalDashboard.overview.riskRating
			.map((r: any) => r.riskRating)
			.filter((r: number | null) => r !== null);
		if (ratings.length === 0) return null;

		const riskLevels: models.RiskLevel[] = dummyData.getRiskLevels();
		const labels: string[] = riskLevels.map((r) => r.level);
		const backgroundColor: string[] = riskLevels.map((r) => r.backgroundColor);
		const criticalities = ratings.map((r: number) =>
			utils.getCriticalityFromRating(r)
		);

		const data = riskLevels.map((risk) => {
			return criticalities.filter((c: string) =>
				utils.matchPhrases(c, risk.level)
			).length;
		});

		return {
			labels,
			datasets: [{ label: 'Risk Rating Breakdown', data, backgroundColor }],
		};
	};

	const handleViewNextTutorial = useCallback(() => {
		dispatch(setActiveTutorial(tutorialConstants.INSPECTION_REPORT));
		navigate(routes.organizationDashboard.fullPath);
	}, [dispatch, navigate]);

	const user = authService.getCurrentUser() as models.User;
	const inspectionBreakdown = getInspectionChartBreakdown();
	const riskBreakdown = getRiskChartBreakdown();

	return (
		<div className={classNames.internalUserDashboard}>
			<WelcomeToExternalUser instructionItems={instructionItems} />

			<Instruction
				isVisible={tutorials.activeTutorial === tutorialConstants.EXTERNAL_DASHBOARD}
				onProceed={handleViewNextTutorial}
				title="Welcome to your dashboard!"
				message="This is where the numbers are! Total number of reports, recent reports, breakdown by inspection type and much more!"
			/>

			<div className="justify-content-between align-items-center d-flex">
				<h1 className={classNames.internalUserDashboardTitle}>
					Welcome, <span>{user.firstName}</span>
				</h1>

				<div className="d-flex align-items-center">
					<div className="mx-4">
						<PDFDownload
							documentLabel="User Guide"
							documentSource={EXTERNAL_USER_GUIDE_URL}
						/>
					</div>

					<div>
						<DashboardSearchBar />
					</div>

					<div className={classNames.internalUserDashboardUserDetails}>
						<div className={classNames.userInfo}>
							<div className={classNames.userInfoTitle}>
								{user.firstName} {user.lastName}
							</div>
							<div className={classNames.userInfoSubtitle}>
								Last Login: {utils.formatTime(user.lastLogin as string)}
							</div>
						</div>
						<div className={classNames.userIcon}>
							<i className="bi bi-person-lines-fill"></i>
						</div>
					</div>
				</div>
			</div>

			<div className="mt-4">
				<SyncTime lastSynced={externalDashboard.lastUpdated as number} />
			</div>

			<DashboardLoader isVisible={externalDashboard.loading} />

			<Conditional isVisible={!externalDashboard.loading}>
				<React.Fragment>
					<div className={classNames.overview}>
						<DashboardOverviewItem
							label="Total Approved Reports"
							value={externalDashboard.overview.totalApprovedReports}
							iconColor="#7a869a"
							icon="kanban-fill"
						/>

						<DashboardOverviewItem
							label="Pending Reports"
							value={externalDashboard.overview.totalPendingReports}
							iconColor="#ab3c3c"
							icon="hourglass-split"
						/>

						<DashboardOverviewItem
							label="Active Reports"
							value={externalDashboard.overview.totalActiveReports}
							iconColor="#04223b"
							icon="alarm-fill"
						/>
					</div>

					<div className={classNames.block}>
						<Conditional isVisible={config.enableRiskRating}>
							<div className={classNames.blockItem}>
								<div className={classNames.blockItemChart}>
									<div>
										<h3 className="text-left">
											Risk Rating <span>Breakdown</span>
										</h3>

										<DBEmptyData
											isVisible={!riskBreakdown}
											message="No risk breakdown is available yet"
										/>

										<Conditional isVisible={!!riskBreakdown}>
											<Pie chartData={riskBreakdown} />
										</Conditional>
									</div>
								</div>
							</div>
						</Conditional>

						<RecentProjects recentProjects={externalDashboard.overview.recentProjects} />
					</div>

					<div className={classNames.overview}>
						<DashboardOverviewItem
							label="Rejected Reports"
							value={externalDashboard.overview.rejectedReports}
							iconColor="#ff0000"
							icon="x-octagon-fill"
						/>

						<DashboardOverviewItem
							label="Accepted Reports"
							value={externalDashboard.overview.acceptedReports}
							icon="check-circle-fill"
							iconColor="#11ab45"
						/>
					</div>

					<div className={classNames.block}>
						<Signature />

						<div className={classNames.blockItem}>
							<div className={classNames.blockItemChart}>
								<div>
									<h3 className="text-left">
										Report Breakdown <span>By Inspection Type</span>
									</h3>

									<DBEmptyData
										isVisible={!inspectionBreakdown}
										message="No inspections have been created yet"
									/>

									<Conditional isVisible={!!inspectionBreakdown}>
										<Pie chartData={inspectionBreakdown} />
									</Conditional>
								</div>
							</div>
						</div>
					</div>
				</React.Fragment>
			</Conditional>
		</div>
	);
};

const instructionItems = [
	{
		title: 'Stay Updated!',
		message: 'Receive constant updates on the status of your projects.',
		animation: notificationBell,
	},
	{
		title: 'View Reports Electronically',
		message: 'View generated reports on any device.',
		animation: pdf,
	},
	{
		title: 'Assess Completed Reports!',
		message: 'Give feedback on completed reports.',
		animation: success,
	},
	{
		title: 'Easy Access!',
		message: 'Access historical data with ease.',
		animation: file,
	},
];

export default ExternalUserDashboard;
